<template>
  <div class="row games-with-genres">
    <GameGenresSelect v-model="genreModel" class="col-3" />
    <div class="col-3"></div>
    <InputSearch
      v-model="gameSearchModel"
      class="col-6 col-lg-4"
      :label="$t('_web_game-search_label', 'Search')"
      :placeholder="$t('_web_game-search_placeholder', 'Start typing game')"
    />
    <div class="col-12 mt-4 games-edit">
      <CardPanel
        class="games-with-genres_games p-4"
        :variant="['input-bg', 'rounded']"
      >
        <div class="row h-100 custom-scrollbar">
          <div class="col-12 col-lg-6 games-with-genres_games_border-right">
            <CardPanel
              :variant="['input-bg']"
              class="sticky-top position-sticky p-0 m-0"
            >
              <TextLabel class="font-weight-bold sticky-top position-sticky">{{
                $t('_web_game-selector_total', 'Total list:')
              }}</TextLabel>
            </CardPanel>
            <div class="games-with-genres_games-list">
              <ListPreloader
                :is-loading="gamesIsLoading"
                :is-crashed="gamesIsCrashed"
                :is-empty-list="!gamesUnselected.length"
                :empty-list-label="
                  $t(
                    '_web_game-selector_unselected-is-empty',
                    'No games in list'
                  )
                "
                @reload="getGames"
              >
                <div
                  slot="list"
                  class="text-truncate games-with-genres_game-items games-with-genres_game-items-cro"
                >
                  <ListItem
                    v-for="{ id, logo, name } in gamesUnselected"
                    :key="id"
                    :avatar-props="generateAvatarProps(logo)"
                    tag="label"
                    :title="name"
                  >
                    <CheckboxGame
                      slot="tail"
                      :val="id"
                      @change="toggleMyGame(true, id)"
                    />
                  </ListItem>
                </div>
              </ListPreloader>
            </div>
          </div>
          <div class="col-12 col-lg-6 selected-game">
            <CardPanel
              :variant="['input-bg']"
              class="sticky-top position-sticky p-0 m-0"
            >
              <TextLabel class="font-weight-bold sticky-top position-sticky">{{
                $t('_web_game-selector_selected', 'Selected Games:')
              }}</TextLabel>
            </CardPanel>
            <div class="games-with-genres_games-list">
              <ListPreloader
                :is-loading="gamesIsLoading"
                :is-empty-list="!gamesSelected.length"
                :empty-list-label="
                  $t(
                    '_web_game-selector_selected-is-empty',
                    'No selected games'
                  )
                "
              >
                <div
                  slot="list"
                  class="text-truncate games-with-genres_game-items games-with-genres_game-items-cro"
                >
                  <ListItem
                    v-for="{ id, logo, name } in gamesSelected"
                    :key="id"
                    :avatar-props="generateAvatarProps(logo)"
                    tag="label"
                    :title="name"
                    :class="'nameShow'"
                  >
                    <CheckboxGame
                      slot="tail"
                      :val="id"
                      checked
                      @change="toggleMyGame(false, id)"
                    />
                  </ListItem>
                </div>
              </ListPreloader>
            </div>
          </div>
        </div>
      </CardPanel>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import InputSearch from '@/components/atoms/form/HeraFormTextInput';
import CardPanel from '@/components/atoms/Panel';
import TextLabel from '@/components/atoms/TextLabel';
import ListPreloader from '@/components/common/HeraListPreloader';
import ListItem from '@/components/common/ListItem';
import CheckboxGame from '@/components/form/HeraFormCheckbox';
import GameGenresSelect from './GameGenresSelectBox';

export default {
  name: 'GamesWithGenres',
  components: {
    CheckboxGame,
    ListItem,
    ListPreloader,
    TextLabel,
    CardPanel,
    InputSearch,
    GameGenresSelect,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      genreModel: null,
      gameSearchModel: '',

      gamesSelectedBefore: [...this.value],

      gamesIsLoading: true,
      gamesIsCrashed: false,
    };
  },
  computed: {
    ...mapGetters(['games', 'genres']),
    gamesSelected() {
      return (
        (this.games &&
          this.games.filter(game => this.value.includes(game.id))) ||
        []
      );
    },
    gameSearchLowerCase() {
      return this.gameSearchModel.trim().toLowerCase();
    },
    gamesUnselected() {
      if (!this.games) {
        return [];
      }

      let gamesUnselected = [...this.games];

      if (this.value.length) {
        gamesUnselected = gamesUnselected.filter(
          game => !this.value.includes(game.id)
        );
      }

      if (this.genreModel) {
        gamesUnselected = gamesUnselected.filter(
          game => game.genres && game.genres.includes(this.genreModel.id)
        ); // filter by genre
      }

      if (this.gameSearchLowerCase) {
        gamesUnselected = gamesUnselected.filter(game =>
          game.nameLowerCase.includes(this.gameSearchLowerCase)
        ); // filter by contains
      }

      return gamesUnselected;
    },
  },
  created() {
    this.getGames();
  },
  methods: {
    ...mapActions(['fetchGames']),
    getGames() {
      this.fetchGames()
        .then(() => {
          this.gamesIsLoading = false;
        })
        .catch(() => {
          this.gamesIsCrashed = true;
        });
    },
    generateAvatarProps(logo) {
      return {
        size: 32,
        type: 'game',
        imgUrl: logo,
      };
    },

    toggleMyGame(isSelected, gameId) {
      const value = [...this.value];
      const gameIndex = value.indexOf(gameId);

      if (isSelected) {
        value.push(gameId);
      } else if (gameIndex !== -1) {
        value.splice(gameIndex, 1);
      }

      this.$emit('input', value);
    },
    // used from $refs
    clear() {
      this.genreModel = null;
      this.gameSearchModel = '';
      this.$emit('input', this.gamesSelectedBefore);
    },
  },
};
</script>

<style lang="scss">
.games-with-genres {
  &_games {
    height: 22rem;
    &_border-right {
      @include auto-rtl(border-right, 2px solid $ph-input-bg);
    }
    .hera-form-checkbox {
      display: inline-flex !important;
    }
    .list-loader {
      line-height: 2rem; // 32px as game avatar
    }
    .list-loader_wrapper {
      justify-content: flex-start !important;
      top: 25%;
    }
  }
  &_game-items {
    font-size: $ph-basic-font-size;
    padding: 0 1rem;
    > label {
      margin: 0 -1rem;
      padding: 0.25rem 0.5rem;
      cursor: pointer;
      &:hover {
        background-color: $ph-card;
      }
    }
  }
}

html[dir='rtl'] {
  .games-with-genres_game-items-cro {
    align-items: center;
    justify-content: center;
    .list-item-title {
      overflow: unset !important;
      padding-left: 15px;
      padding-right: 10px;
      max-width: 200px;
    }
  }
}

.games-with-genres_game-items-cro {
  align-items: center;
  justify-content: center;
  .list-item-title {
    overflow: unset !important;
    padding-right: 15px;
    max-width: 200px;
  }
  .hera-form-checkbox {
    margin-bottom: 0;
  }
}
@media (max-width: 767px) {
  .games-edit {
    display: flex;
    flex-direction: column;
    .games-with-genres_games_border-right {
      order: 2;
    }
  }
  .selected-game {
    .list-loader {
      min-height: unset;
    }
  }
}
</style>
